import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Mail } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { API } from "../config/api";

// Forgot-password landing — single email field. We INTENTIONALLY show the
// same success message regardless of whether the email is registered (the
// backend enforces this too) so the page can't be used to enumerate accounts.
const ForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSubmitted(true);
    } catch (err) {
      // 429 from rate limit is the only path where we surface a specific
      // error — every other failure folds into the generic success state
      // so attackers can't tell signal from noise.
      if (err.response?.status === 429) {
        toast.error(err.response?.data?.detail || "Too many attempts. Try again later.");
      } else {
        setSubmitted(true);
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-start justify-center px-4 pt-20" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-8" data-testid="back-home-link">
          <ArrowLeft className="h-4 w-4" /> Back home
        </Link>

        <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 sm:p-8">
          {!submitted ? (
            <>
              <h1 className="text-2xl font-semibold text-white mb-2">Forgot your password?</h1>
              <p className="text-gray-400 text-sm mb-6">
                Enter the email you signed up with. We'll send you a link to set a new password — good for 30 minutes.
              </p>
              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="fp-email" className="text-gray-300 text-sm">Email</Label>
                  <Input
                    id="fp-email" type="email" autoComplete="email"
                    autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="bg-gray-800 border-gray-700 text-white mt-1"
                    placeholder="you@example.com"
                    required
                    data-testid="fp-email-input"
                  />
                </div>
                <Button
                  type="submit" disabled={loading || !email}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="fp-submit-btn"
                >
                  {loading ? "Sending..." : "Send reset link"}
                </Button>
              </form>
            </>
          ) : (
            <div data-testid="fp-success">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center">
                  <Mail className="h-5 w-5 text-emerald-400" />
                </div>
                <h1 className="text-xl font-semibold text-white">Check your inbox</h1>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                If <span className="text-white font-medium">{email}</span> is registered, a reset link is on its way. It expires in 30 minutes.
              </p>
              <p className="text-gray-500 text-xs mt-4">
                Didn't get it? Check spam, or wait a couple of minutes and{" "}
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  className="text-emerald-400 hover:text-emerald-300 underline"
                  data-testid="fp-try-again-btn"
                >
                  try again
                </button>.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
